import { queryOptions } from '@tanstack/react-query';
import { apiFetch } from '../api-client';

export interface TenantRow {
  id: string;
  name: string;
  status: 'active' | 'pending' | 'suspended';
  plan: string;
  tokenUsage: number;
  tokenLimit: number;
  lastActivity: string; // ISO timestamp
}

export interface CustomerStats {
  total: number;
  totalDelta: number; // e.g. +3 this month
  active: number;
  activePct: number; // e.g. 83 (percent of total)
  needsAttention: number;
  attentionDelta: number; // e.g. +1 since yesterday
  periodTokens: number | null;
  tokenLimit: number | null; // allocated limit
}

export interface CustomersParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  status?: string;
}

export interface CustomersResponse {
  stats: CustomerStats;
  tenants: TenantRow[];
  total: number;
  page: number;
  totalPages: number;
}

async function fetchCustomers(
  params: CustomersParams,
): Promise<CustomersResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.sort) qs.set('sort', params.sort);
  if (params.status) qs.set('status', params.status);
  const res = await apiFetch(`/api/console/customers?${qs.toString()}`);
  if (!res.ok) throw new Error('Failed to load customers');
  return res.json();
}

export function customersQueryOptions(params: CustomersParams = {}) {
  return queryOptions({
    queryKey: ['customers', params],
    queryFn: () => fetchCustomers(params),
  });
}

export interface TenantDetail {
  id: string;
  hostAccountId: string;
  status: 'active' | 'pending' | 'suspended';
  credentialMode: 'platform_managed' | 'byok';
  createdAt: string;
  planId: string;
  planName: string;
  includedTokens: number;
  overageRate: string;
  periodUsage: number;
  dailyUsage: { date: string; tokens: number }[];
}

async function fetchCustomer(id: string): Promise<TenantDetail> {
  const res = await apiFetch(`/api/console/customers/${id}`);
  if (!res.ok) throw new Error('Failed to load customer');
  const json = await res.json();
  return json.customer;
}

export function customerQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id),
  });
}

export interface CustomerSkill {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  updatedAt: string;
}

async function fetchCustomerSkills(
  id: string,
  search: string,
  status: string,
): Promise<CustomerSkill[]> {
  const qs = new URLSearchParams();
  if (search) qs.set('search', search);
  if (status) qs.set('status', status);
  const res = await apiFetch(
    `/api/console/customers/${id}/skills?${qs.toString()}`,
  );
  if (!res.ok) throw new Error('Failed to load skills');
  const json = await res.json();
  return json.skills;
}

export function customerSkillsQueryOptions(
  id: string,
  search = '',
  status = '',
) {
  return queryOptions({
    queryKey: ['customer-skills', id, search, status],
    queryFn: () => fetchCustomerSkills(id, search, status),
  });
}

export interface AuditEntry {
  id: string;
  createdAt: string;
  actorType: 'user' | 'operator' | 'system';
  actorName: string;
  action: string;
  tag: string | null;
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
  page: number;
  totalPages: number;
}

async function fetchCustomerAuditLog(
  id: string,
  page: number,
): Promise<AuditLogResponse> {
  const res = await apiFetch(
    `/api/console/customers/${id}/audit-log?page=${page}&limit=20`,
  );
  if (!res.ok) throw new Error('Failed to load audit log');
  return res.json();
}

export function customerAuditLogQueryOptions(id: string, page = 1) {
  return queryOptions({
    queryKey: ['customer-audit-log', id, page],
    queryFn: () => fetchCustomerAuditLog(id, page),
  });
}

export interface UsageDataPoint {
  date: string;
  userId: string;
  tokens: number;
}

export interface UsageUser {
  id: string;
  hostUserId: string;
  totalTokens: number;
}

export interface CustomerUsageResponse {
  dailyByUser: UsageDataPoint[];
  users: UsageUser[];
}

async function fetchCustomerUsage(
  id: string,
  days: number,
): Promise<CustomerUsageResponse> {
  const res = await apiFetch(`/api/console/customers/${id}/usage?days=${days}`);
  if (!res.ok) throw new Error('Failed to load usage');
  return res.json();
}

export function customerUsageQueryOptions(id: string, days = 30) {
  return queryOptions({
    queryKey: ['customer-usage', id, days],
    queryFn: () => fetchCustomerUsage(id, days),
  });
}

export interface HostConnectionData {
  oauth: {
    status: 'connected' | 'disconnected' | 'pending';
    refreshedAt: string | null;
    expiresAt: string | null;
  };
  signingSecret: {
    exists: boolean;
    maskedValue: string | null;
    lastRotatedAt: string | null;
  };
  suiteApp: {
    bundleId: string;
    installedVersion: string | null;
    requiredVersion: string;
    installDate: string | null;
  };
  installInstructions: {
    apiEndpoint: string;
    customerId: string;
    bundleId: string;
  };
}

async function fetchHostConnection(id: string): Promise<HostConnectionData> {
  const res = await apiFetch(`/api/console/customers/${id}/host-connection`);
  if (!res.ok) throw new Error('Failed to load host connection');
  return res.json();
}

export function hostConnectionQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['host-connection', id],
    queryFn: () => fetchHostConnection(id),
  });
}

export async function rotateSecret(id: string): Promise<{ secret: string }> {
  const res = await apiFetch(`/api/console/customers/${id}/secrets/rotate`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Failed to rotate secret');
  return res.json();
}

export async function revealSecret(id: string): Promise<{ secret: string }> {
  const res = await apiFetch(`/api/console/customers/${id}/secrets/signing`);
  if (!res.ok) throw new Error('Failed to reveal secret');
  return res.json();
}

export async function reinitiateOAuth(id: string): Promise<void> {
  const res = await apiFetch(
    `/api/console/customers/${id}/host-connection/reinitiate`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error('Failed to re-initiate OAuth');
}

export interface NetsuiteSigningKeyMeta {
  provisioned: boolean;
  updatedAt: string | null;
  fingerprint: string | null;
  modulusBits: number | null;
}

async function fetchNetsuiteSigningKeyMeta(
  id: string,
): Promise<NetsuiteSigningKeyMeta> {
  const res = await apiFetch(
    `/api/console/customers/${id}/netsuite-signing-key`,
  );
  if (!res.ok) throw new Error('Failed to load signing key status');
  return res.json();
}

export function netsuiteSigningKeyQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['netsuite-signing-key', id],
    queryFn: () => fetchNetsuiteSigningKeyMeta(id),
  });
}

export async function uploadNetsuiteSigningKey(
  id: string,
  privateKeyPem: string,
): Promise<{ fingerprint: string; modulusBits: number | null }> {
  const res = await apiFetch(
    `/api/console/customers/${id}/netsuite-signing-key`,
    { method: 'PUT', body: JSON.stringify({ privateKeyPem }) },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to upload signing key');
  }
  return res.json();
}

export interface NetsuiteConfig {
  downstreamType: 'mock' | 'restlet' | 'native_mcp';
  restletUrl: string;
  tokenEndpoint: string;
  oauthClientId: string;
  oauthCertId: string;
  oauthScope: string;
  nativeMcpUrl: string;
}

async function fetchNetsuiteConfig(id: string): Promise<NetsuiteConfig> {
  const res = await apiFetch(`/api/console/customers/${id}/netsuite-config`);
  if (!res.ok) throw new Error('Failed to load NetSuite config');
  return res.json();
}

export function netsuiteConfigQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['netsuite-config', id],
    queryFn: () => fetchNetsuiteConfig(id),
  });
}

export async function updateNetsuiteConfig(
  id: string,
  config: NetsuiteConfig,
): Promise<void> {
  const res = await apiFetch(`/api/console/customers/${id}/netsuite-config`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? 'Failed to save NetSuite config');
  }
}
